"use client";

import { useState, type ButtonHTMLAttributes, type MouseEvent } from "react";
import { Check, Copy, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ButtonCopyToLLMProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  tooltipLabel?: string;
  getCopyText?: () => string | Promise<string>;
  onBeforeCopy?: () => boolean | Promise<boolean>;
}

export function ButtonCopyToLLM({
  label = "Copy to LLM",
  tooltipLabel = "Copy this prompt to improve this section.",
  className = "",
  onClick,
  getCopyText,
  onBeforeCopy,
  ...props
}: ButtonCopyToLLMProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const writeToClipboard = async (text: string) => {
    const content = text?.toString().trim();
    if (!content) {
      toast.error("Nothing to copy");
      return;
    }

    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(content);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = content;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    setShowConfirmation(true);
  };

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    // Prevent collapsing the parent section
    e.stopPropagation();
    if (onClick) onClick(e);

    if (!getCopyText) {
      return;
    }

    try {
      if (onBeforeCopy) {
        const shouldContinue = await Promise.resolve(onBeforeCopy());
        if (shouldContinue === false) {
          return;
        }
      }
      const text = await Promise.resolve(getCopyText());
      await writeToClipboard(text ?? "");
    } catch (error) {
      toast.error("Copy failed");
      console.error("Copy to LLM failed", error);
    }
  };

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`shrink-0 inline-flex items-center gap-1.5 ${className}`}
          aria-label={tooltipLabel}
          onClick={handleClick}
          {...props}
        >
          <Copy className="h-4 w-4" aria-hidden />
          <span>{label}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipLabel}</TooltipContent>
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-md p-8 max-h-[90vh] overflow-y-auto">
          <div className="flex flex-col items-center text-center space-y-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500">
              <Check className="h-6 w-6 text-white" strokeWidth={3} />
            </div>
            <DialogHeader className="space-y-3 text-center w-full">
              <DialogTitle className="text-2xl font-semibold text-foreground w-full text-center">
                Prompt copied
              </DialogTitle>
              <DialogDescription className="text-base text-center">
                Paste this prompt into your coding agent to start implementing
                the fixes for this section.
              </DialogDescription>
            </DialogHeader>
            <div className="w-full rounded-lg bg-amber-500/10 p-4 text-sm text-amber-800 flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <span className="font-semibold text-amber-900">
                Review every proposed change carefully and only accept diffs
                that look correct for your site.
              </span>
            </div>
          </div>
          <DialogFooter className="mt-8">
            <Button onClick={() => setShowConfirmation(false)} className="w-full">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tooltip>
  );
}
